import React, { useState, useEffect } from "react";
// eslint-disable-next-line import/no-extraneous-dependencies
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  MdContentPaste,
  MdPeople,
  MdStar,
  MdAccessTime,
  MdCheckCircle,
  MdWarning,
  MdLocationOn,
  MdError,
} from "react-icons/md";
// eslint-disable-next-line import/no-duplicates
import { endOfMonth, format, startOfMonth } from "date-fns";
// eslint-disable-next-line import/no-duplicates
import { ptBR } from "date-fns/locale";
import { useNavigate } from "react-router-dom";
import { api } from "../../services/api";
import { useAuth } from "../../contexts/AuthContext";

interface DashboardMetrics {
  reportsToday: number;
  reportsWeek: number;
  reportsMonth: number;
  activeTechnicians: number;
  topTechnician: {
    name: string;
    count: number;
  };
  averageVisitTime: string;
  completionRate: number;
  emergencyCustomers: number;
  openEmergencyObservations: number;
  averageSectorsPerReport: number;
}

interface ChartData {
  date: string;
  reports: number;
}

interface MetricCardProps {
  title: string;
  value: string | number;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  color: string;
  subtitle?: string;
}

function MetricCard({
  title,
  value,
  icon: Icon,
  color,
  subtitle,
}: MetricCardProps) {
  // Mapear cores de fundo para cores de gradiente, sombra e texto
  const colorMap: Record<string, { gradient: string; shadow: string; text: string; bgAccent: string }> = {
    "bg-blue-500": {
      gradient: "from-blue-500 to-blue-600",
      shadow: "shadow-blue-500/30",
      text: "from-blue-600 to-blue-700",
      bgAccent: "from-blue-500/10 to-blue-600/5"
    },
    "bg-green-500": {
      gradient: "from-green-500 to-emerald-500",
      shadow: "shadow-green-500/30",
      text: "from-green-600 to-emerald-600",
      bgAccent: "from-green-500/10 to-emerald-500/5"
    },
    "bg-purple-500": {
      gradient: "from-purple-500 to-indigo-500",
      shadow: "shadow-purple-500/30",
      text: "from-purple-600 to-indigo-600",
      bgAccent: "from-purple-500/10 to-indigo-500/5"
    },
    "bg-orange-500": {
      gradient: "from-orange-500 to-amber-500",
      shadow: "shadow-orange-500/30",
      text: "from-orange-600 to-amber-600",
      bgAccent: "from-orange-500/10 to-amber-500/5"
    },
    "bg-yellow-500": {
      gradient: "from-yellow-500 to-amber-500",
      shadow: "shadow-yellow-500/30",
      text: "from-yellow-600 to-amber-600",
      bgAccent: "from-yellow-500/10 to-amber-500/5"
    },
    "bg-indigo-500": {
      gradient: "from-indigo-500 to-purple-500",
      shadow: "shadow-indigo-500/30",
      text: "from-indigo-600 to-purple-600",
      bgAccent: "from-indigo-500/10 to-purple-500/5"
    },
    "bg-emerald-500": {
      gradient: "from-emerald-500 to-teal-500",
      shadow: "shadow-emerald-500/30",
      text: "from-emerald-600 to-teal-600",
      bgAccent: "from-emerald-500/10 to-teal-500/5"
    },
    "bg-pink-500": {
      gradient: "from-pink-500 to-rose-500",
      shadow: "shadow-pink-500/30",
      text: "from-pink-600 to-rose-600",
      bgAccent: "from-pink-500/10 to-rose-500/5"
    },
    "bg-cyan-500": {
      gradient: "from-cyan-500 to-teal-500",
      shadow: "shadow-cyan-500/30",
      text: "from-cyan-600 to-teal-600",
      bgAccent: "from-cyan-500/10 to-teal-500/5"
    },
    "bg-gray-500": {
      gradient: "from-gray-500 to-slate-500",
      shadow: "shadow-gray-500/30",
      text: "from-gray-600 to-slate-600",
      bgAccent: "from-gray-500/10 to-slate-500/5"
    },
    "bg-red-500": {
      gradient: "from-red-500 to-rose-500",
      shadow: "shadow-red-500/30",
      text: "from-red-600 to-rose-600",
      bgAccent: "from-red-500/10 to-rose-500/5"
    },
  };

  const colorClasses = colorMap[color] || {
    gradient: "from-[#0085BD] to-[#006a96]",
    shadow: "shadow-[#0085BD]/30",
    text: "from-[#0085BD] to-[#006a96]",
    bgAccent: "from-[#0085BD]/10 to-[#006a96]/5"
  };

  return (
    <div className="group relative bg-white rounded-2xl p-5 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 overflow-hidden">
      {/* Elemento decorativo no canto */}
      <div className={`absolute top-0 right-0 w-20 h-20 bg-gradient-to-br ${colorClasses.bgAccent} rounded-bl-full`} />

      <div className="relative flex items-center gap-4">
        {/* Ícone com gradiente */}
        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${colorClasses.gradient} flex items-center justify-center shadow-lg ${colorClasses.shadow} group-hover:scale-110 transition-transform duration-300`}>
          <Icon size={24} className="text-white" />
        </div>

        <div className="flex-1 min-w-0">
          {/* Valor com gradiente de texto */}
          <p className={`text-2xl font-bold bg-gradient-to-r ${colorClasses.text} bg-clip-text text-transparent`}>
            {value}
          </p>
          <p className="text-sm text-gray-500 font-medium truncate">{title}</p>
          {subtitle && (
            <p className="text-xs text-gray-400 mt-0.5 truncate">{subtitle}</p>
          )}
        </div>
      </div>
    </div>
  );
}

export function Dashboard() {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  // Determinar permissões e tipo padrão
  const canViewTechnicalVisit = hasPermission("tech-visit-reports:view-technical-visit");
  const canViewDelivery = hasPermission("tech-visit-reports:view-delivery");
  const hasBothPermissions = canViewTechnicalVisit && canViewDelivery;

  // Definir tipo padrão baseado nas permissões
  const getDefaultType = (): "technical_visit" | "delivery" => {
    if (canViewTechnicalVisit && !canViewDelivery) return "technical_visit";
    if (canViewDelivery && !canViewTechnicalVisit) return "delivery";
    return "technical_visit"; // Padrão se tiver ambas ou nenhuma
  };

  const [reportType, setReportType] = useState<"technical_visit" | "delivery">(getDefaultType());
  const [metrics, setMetrics] = useState<DashboardMetrics>({
    reportsToday: 0,
    reportsWeek: 0,
    reportsMonth: 0,
    activeTechnicians: 0,
    topTechnician: { name: "N/A", count: 0 },
    averageVisitTime: "0min",
    completionRate: 0,
    emergencyCustomers: 0,
    openEmergencyObservations: 0,
    averageSectorsPerReport: 0,
  });

  const [chartData, setChartData] = useState<ChartData[]>([]);
  const [dateRange, setDateRange] = useState({
    startDate: format(startOfMonth(new Date()), "yyyy-MM-dd"),
    endDate: format(endOfMonth(new Date()), "yyyy-MM-dd"),
  });
  const [loading, setLoading] = useState(true);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [chartLoading, setChartLoading] = useState(false);

  const fetchMetricsAndChart = async (type: string) => {
    try {
      setMetricsLoading(true);

      // Buscar métricas e gráfico em paralelo
      const [metricsResponse, chartResponse] = await Promise.all([
        api.get("/dashboard/metrics", { params: { type } }),
        api.get("/dashboard/chart", {
          params: {
            startDate: dateRange.startDate,
            endDate: dateRange.endDate,
            type,
          },
        }),
      ]);

      setMetrics(metricsResponse.data);
      setChartData(chartResponse.data);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Erro ao buscar dados do dashboard:", error);
    } finally {
      setMetricsLoading(false);
    }
  };

  const fetchChartData = async () => {
    try {
      setChartLoading(true);
      const chartResponse = await api.get("/dashboard/chart", {
        params: { ...dateRange, type: reportType },
      });
      setChartData(chartResponse.data);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Erro ao buscar dados do gráfico:", error);
    } finally {
      setChartLoading(false);
    }
  };

  useEffect(() => {
    // Carrega dados iniciais apenas uma vez
    const loadInitialData = async () => {
      try {
        setLoading(true);
        await fetchMetricsAndChart(reportType);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Erro ao buscar dados do dashboard:", error);
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, []); // Executa apenas uma vez no carregamento inicial

  useEffect(() => {
    // Quando o tipo de relatório mudar (após o carregamento inicial)
    if (!loading) {
      fetchMetricsAndChart(reportType);
    }
  }, [reportType]); // Recarrega quando o tipo de relatório mudar

  const handleDateRangeChange = (field: string, value: string) => {
    setDateRange(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0085BD]" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-[#0085BD]">Dashboard</h1>
        <p className="text-sm text-[#7FA5B2] mt-1">
          {reportType === "technical_visit"
            ? "Visão geral dos relatórios de visita técnica"
            : "Visão geral dos relatórios de entrega"}
        </p>
      </div>

      {/* Toggle de Tipo de Relatório - Mostrar apenas se tiver ambas permissões */}
      {hasBothPermissions && (
      <div className="flex justify-start items-center gap-4">
        <div className="inline-flex items-center bg-white rounded-full shadow-md border border-gray-200 p-1">
          <button
            onClick={() => setReportType("technical_visit")}
            disabled={metricsLoading}
            className={`px-6 py-2 rounded-full font-medium text-sm transition-all duration-300 ${
              reportType === "technical_visit"
                ? "bg-gradient-to-r from-[#0085BD] to-[#006a96] text-white shadow-lg"
                : "text-gray-600 hover:text-gray-800"
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            Visita Técnica
          </button>
          <button
            onClick={() => setReportType("delivery")}
            disabled={metricsLoading}
            className={`px-6 py-2 rounded-full font-medium text-sm transition-all duration-300 ${
              reportType === "delivery"
                ? "bg-gradient-to-r from-[#0085BD] to-[#006a96] text-white shadow-lg"
                : "text-gray-600 hover:text-gray-800"
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            Entrega
          </button>
        </div>
        {metricsLoading && (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-[#0085BD]/30 border-t-[#0085BD]" />
            <span>Atualizando...</span>
          </div>
        )}
      </div>
      )}

      {/* Cards de Métricas */}
      <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 transition-opacity duration-300 ${metricsLoading ? 'opacity-50' : 'opacity-100'}`}>
        <MetricCard
          title="Relatórios Hoje"
          value={metrics.reportsToday}
          icon={MdContentPaste}
          color="bg-blue-500"
        />
        <MetricCard
          title="Relatórios Semana"
          value={metrics.reportsWeek}
          icon={MdContentPaste}
          color="bg-green-500"
        />
        <MetricCard
          title="Relatórios Mensal"
          value={metrics.reportsMonth}
          icon={MdContentPaste}
          color="bg-purple-500"
        />
        <MetricCard
          title="Técnicos Ativos"
          value={metrics.activeTechnicians}
          icon={MdPeople}
          color="bg-orange-500"
        />
      </div>

      {/* Segunda linha de cards */}
      <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 transition-opacity duration-300 ${metricsLoading ? 'opacity-50' : 'opacity-100'}`}>
        <MetricCard
          title="Técnico do Mês"
          value={metrics.topTechnician.name}
          icon={MdStar}
          color="bg-yellow-500"
          subtitle={`${metrics.topTechnician.count} relatórios`}
        />
        <MetricCard
          title="Tempo Médio"
          value={metrics.averageVisitTime}
          icon={MdAccessTime}
          color="bg-indigo-500"
          subtitle="Por visita"
        />
        <MetricCard
          title="Taxa de Conclusão"
          value={`${metrics.completionRate}%`}
          icon={MdCheckCircle}
          color="bg-emerald-500"
        />
        {reportType === "technical_visit" && (
          <MetricCard
            title="Média Setores"
            value={metrics.averageSectorsPerReport.toFixed(1).replace('.', ',')}
            icon={MdLocationOn}
            color="bg-pink-500"
            subtitle="Por relatório"
          />
        )}
      </div>

      {/* Análise de Qualidade */}
      <div className={`grid grid-cols-1 sm:grid-cols-2 gap-6 transition-opacity duration-300 ${metricsLoading ? 'opacity-50' : 'opacity-100'}`}>
        <MetricCard
          title="Clientes em Emergência"
          value={metrics.emergencyCustomers}
          icon={MdWarning}
          color="bg-orange-500"
          subtitle="Necessitam atenção urgente"
        />
        <MetricCard
          title="Observações Abertas"
          value={metrics.openEmergencyObservations}
          icon={MdError}
          color="bg-red-500"
          subtitle="Em clientes de emergência"
        />
      </div>

      {/* Gráfico Temporal - Modernizado */}
      <div className={`bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden transition-opacity duration-300 ${metricsLoading ? 'opacity-50' : 'opacity-100'}`}>
        {/* Header com Gradiente */}
        <div className="bg-gradient-to-r from-[#0085BD] to-[#006a96] px-6 py-4">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">
                  Relatórios por Período
                </h2>
                <p className="text-white/70 text-sm">Acompanhe a evolução dos relatórios</p>
              </div>
            </div>

            {/* Seletores de Data */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-end gap-3">
              <div className="flex flex-col">
                <label
                  htmlFor="startDate"
                  className="text-xs text-white/80 mb-1.5 font-medium"
                >
                  Data Início
                </label>
                <input
                  id="startDate"
                  type="date"
                  value={dateRange.startDate}
                  onChange={e =>
                    handleDateRangeChange("startDate", e.target.value)
                  }
                  className="px-3 py-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg text-sm text-white focus:ring-2 focus:ring-white/30 focus:border-transparent placeholder-white/50 [color-scheme:dark]"
                />
              </div>
              <div className="flex flex-col">
                <label htmlFor="endDate" className="text-xs text-white/80 mb-1.5 font-medium">
                  Data Fim
                </label>
                <input
                  id="endDate"
                  type="date"
                  value={dateRange.endDate}
                  onChange={e => handleDateRangeChange("endDate", e.target.value)}
                  className="px-3 py-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg text-sm text-white focus:ring-2 focus:ring-white/30 focus:border-transparent [color-scheme:dark]"
                />
              </div>
              <button
                onClick={fetchChartData}
                disabled={chartLoading}
                className="px-5 py-2 bg-white text-[#0085BD] rounded-lg hover:bg-white/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-medium shadow-lg shadow-black/10"
              >
                {chartLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-[#0085BD]/30 border-t-[#0085BD]" />
                    <span className="text-sm">Carregando...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    <span className="text-sm">Atualizar</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Área do Gráfico */}
        <div className="p-6">
          {/* Stats rápidos do período */}
          {chartData.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
              <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-4 border border-blue-100">
                <p className="text-xs text-blue-600 font-medium uppercase tracking-wide">Total no Período</p>
                <p className="text-2xl font-bold text-blue-700 mt-1">
                  {chartData.reduce((sum, item) => sum + item.reports, 0)}
                </p>
              </div>
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-4 border border-green-100">
                <p className="text-xs text-green-600 font-medium uppercase tracking-wide">Média Diária</p>
                <p className="text-2xl font-bold text-green-700 mt-1">
                  {(chartData.reduce((sum, item) => sum + item.reports, 0) / chartData.length).toFixed(1).replace('.', ',')}
                </p>
              </div>
              <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-4 border border-purple-100">
                <p className="text-xs text-purple-600 font-medium uppercase tracking-wide">Dia com Mais</p>
                <p className="text-2xl font-bold text-purple-700 mt-1">
                  {Math.max(...chartData.map(item => item.reports))}
                </p>
              </div>
              <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-4 border border-orange-100">
                <p className="text-xs text-orange-600 font-medium uppercase tracking-wide">Dias Ativos</p>
                <p className="text-2xl font-bold text-orange-700 mt-1">
                  {chartData.filter(item => item.reports > 0).length}
                </p>
              </div>
            </div>
          )}

          {/* Gráfico */}
          <div className="h-80 bg-gradient-to-br from-gray-50/50 to-white rounded-xl p-4 border border-gray-100">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                <defs>
                  <linearGradient id="colorReports" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#0085BD" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#0085BD" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
                <XAxis
                  dataKey="date"
                  tickFormatter={value =>
                    format(new Date(value), "dd/MM", { locale: ptBR })
                  }
                  tick={{ fill: '#6B7280', fontSize: 12 }}
                  axisLine={{ stroke: '#E5E7EB' }}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fill: '#6B7280', fontSize: 12 }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
                    padding: '12px 16px'
                  }}
                  labelStyle={{ color: '#374151', fontWeight: 600, marginBottom: '4px' }}
                  itemStyle={{ color: '#0085BD' }}
                  labelFormatter={value =>
                    format(new Date(value), "dd 'de' MMMM", { locale: ptBR })
                  }
                  formatter={(value: number) => [`${value} relatório${value !== 1 ? 's' : ''}`, '']}
                />
                <Line
                  type="monotone"
                  dataKey="reports"
                  stroke="#0085BD"
                  strokeWidth={3}
                  dot={{ fill: "#0085BD", strokeWidth: 2, stroke: 'white', r: 4 }}
                  activeDot={{ fill: "#0085BD", strokeWidth: 3, stroke: 'white', r: 6 }}
                  name="Relatórios"
                  fill="url(#colorReports)"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Legenda customizada */}
          <div className="flex items-center justify-center gap-6 mt-4">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-[#0085BD]"></div>
              <span className="text-sm text-gray-600">Relatórios realizados</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
